import React from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { ChoiceField } from '@ns/plugin-picker-shared';
import { keepPreviousData } from '@tanstack/react-query';

import {
  buildServicePrincipalFormData,
  selectServicePrincipalPage,
  type ServicePrincipalOption,
} from './directory-options';
import {
  SERVICE_PRINCIPAL_DIRECTORY_SOURCE_KEY,
  servicePrincipalListOptions,
} from './directory-query';
import type { EntraServicePrincipal } from './types';
import { useServicePrincipals } from './use-directory';
import {
  type DirectoryPickerSettings,
  useDirectoryPicker,
} from './use-directory-picker';

/** Searches the service-principal collection for one settled term. */
function useServicePrincipalSearch(term: string) {
  return useServicePrincipals(servicePrincipalListOptions({ term }), {
    placeholderData: keepPreviousData,
    select: selectServicePrincipalPage,
  });
}

const SERVICE_PRINCIPAL_PICKER: DirectoryPickerSettings<ServicePrincipalOption> =
  {
    droppedRowsLabel: 'Entra service principals',
    noun: 'service principals',
    useSearch: useServicePrincipalSearch,
  };

/**
 * Renders the fixed Microsoft Entra service-principal picker for a scaffolder field.
 *
 * @remarks
 * Presentation keeps `servicePrincipalType` only long enough to distinguish
 * equal display names. The public form value carries exactly `id`, `appId`,
 * `displayName` and `type`.
 */
export function EntraServicePrincipalPickerComponent(
  props: FieldExtensionComponentProps<EntraServicePrincipal>,
): React.ReactElement {
  const picker = useDirectoryPicker(SERVICE_PRINCIPAL_PICKER);
  const selected = props.formData;
  return (
    <ChoiceField<ServicePrincipalOption>
      description={props.schema.description}
      emptyText={picker.emptyText}
      getKey={item => item.id}
      getLabel={item =>
        `${item.displayName} (${item.servicePrincipalType}, ${item.appId})`
      }
      items={picker.items}
      label={props.schema.title ?? props.name}
      onSelect={item =>
        props.onChange(item && buildServicePrincipalFormData(item))
      }
      remoteSearch={picker.remoteSearch}
      required={props.required}
      selectedKey={typeof selected?.id === 'string' ? selected.id : null}
      selectedLabel={
        typeof selected?.displayName === 'string' ? selected.displayName : null
      }
      sourceKey={SERVICE_PRINCIPAL_DIRECTORY_SOURCE_KEY}
      state={picker.state}
    />
  );
}
